<template>
  <div class="row">
    <div class="col-md-8 offset-md-2">
      <div class="row">
        <div class="col-md-6">
          <contact-card
            :contact="contact"
            v-if="contact"
            :link="null"
            v-on:contact-updated="refreshContact"
            v-on:contact-deleted="handleContactDeletion"
          ></contact-card>
        </div>

        <div class="col-md-6">
          <card v-if="contact">
            <h5 slot="header">Details</h5>

            <div>
              <h6>Contact information</h6>
              <!-- email -->
              <ul class="fa-ul" v-if="contact.emails && contact.emails.length > 0">
                <li v-for="(email, index) in contact.emails" v-bind:key="index">
                  <i class="fa-li fa fa-envelope"></i>
                  <a :href="`mailto:${email.address}`">{{ email.address }}</a>
                  <span v-if="email.label" class="text-muted ml-4">
                    <i class="fa fa-tag mr-1"></i>
                    {{ email.label }}
                  </span>
                  <span v-if="email.isPrimary" class="text-muted ml-4">
                    <i class="fa fa-check mr-1"></i> Primary
                  </span>
                </li>
              </ul>
              <!-- <p v-else class="text-muted">No email address.</p> -->

              <!-- phone -->
              <ul class="fa-ul" v-if="contact.phones && contact.phones.length > 0">
                <li v-for="(phone, index) in contact.phones" v-bind:key="index">
                  <i class="fa-li fa fa-phone"></i>
                  <a :href="`tel:${phone.number}`">{{ phone.number }}</a>
                  <span v-if="phone.label" class="text-muted ml-4">
                    <i class="fa fa-tag mr-1"></i>
                    {{ phone.label }}
                  </span>
                  <span v-if="phone.isPrimary" class="text-muted ml-4">
                    <i class="fa fa-check mr-1"></i> Primary
                  </span>
                </li>
              </ul>
              <!-- <p v-else class="text-muted">No phone numbers.</p> -->

              <!-- Note -->
              <h6 class="mt-5">Notes</h6>
              <vue-markdown :source="contact.note" v-if="contact.note"></vue-markdown>
              <p v-else class="text-muted">No notes saved.</p>
            </div>
          </card>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <card v-if="contact">
            <h5 slot="header">Next catch-up</h5>

            <div v-if="!contact.nextCatchup" class="text-center">
              <i class="fa fa-frown-o text-muted" style="font-size: 7em"></i>
              <p class="lead mt-3">
                No catch-ups planned with
                {{ contact.firstName }}.
              </p>

              <button v-on:click="displayCreateCatchupModal = true" class="btn btn-primary btn-lg">
                <i class="fa fa-calendar mr-2"></i> Schedule catch-up now
              </button>
            </div>
            <div v-else>
              <span v-if="contact.nextCatchup.type">
                <span v-if="contact.nextCatchup.type === 'in_person'">
                  <i class="fa fa-users mr-2"></i> Meetup
                </span>
                <span v-else-if="contact.nextCatchup.type === 'phone_call'">
                  <i class="fa fa-phone mr-2"></i> Phone call
                </span>
                <span v-else-if="contact.nextCatchup.type === 'video_call'">
                  <i class="fa fa-video-camera mr-2"></i> Video call
                </span>
                <span v-else-if="contact.nextCatchup.type === 'messaging'">
                  <i class="fa fa-comments-o mr-2"></i> Messaging session
                </span>
                <span v-else-if="contact.nextCatchup.type === 'email'">
                  <i class="fa fa-envelope mr-2"></i> Email session
                </span>
                <span v-else-if="contact.nextCatchup.type === 'other'">
                  <i class="fa fa-circle mr-2"></i>
                </span>
              </span>
              {{
              contact.nextCatchup.date
              | moment("calendar", "now", calendarFormats)
              }}
              <span
                v-if="contact.nextCatchup.location"
              >at {{ contact.nextCatchup.location }}</span>

              <h6 class="mt-5">Notes</h6>
              <vue-markdown
                :source="contact.nextCatchup.beforeNote"
                v-if="contact.nextCatchup.beforeNote"
              ></vue-markdown>
              <br />
              <vue-markdown
                :source="contact.nextCatchup.afterNote"
                v-if="contact.nextCatchup.afterNote"
              ></vue-markdown>

              <h6 class="mt-5">Actions</h6>

              <add-to-calendar
                :title="
                  `Catch up with ${contact.firstName} ${
                    contact.lastName
                  } (${$options.filters.humanize(contact.nextCatchup.type)})`
                "
                :location="contact.nextCatchup.location || ''"
                :start="
                  moment(
                    `${contact.nextCatchup.date}T12:00:00Z`,
                    'YYYY-MM-DDTHH:mm:ssZ'
                  ).toDate()
                "
                :end="
                  moment(
                    `${contact.nextCatchup.date}T13:00:00Z`,
                    'YYYY-MM-DDTHH:mm:ssZ'
                  ).toDate()
                "
                :details="contact.nextCatchup.beforeNote"
                inline-template
              >
                <span>
                  <google-calendar id="google-calendar" class="btn btn-default">
                    <i class="fa fa-google mr-2"></i> Add to Google calendar
                  </google-calendar>&nbsp;
                  <microsoft-calendar id="microsoft-calendar" class="btn btn-default">
                    <i class="fa fa-windows mr-2"></i> Add to Microsoft live
                    calendar
                  </microsoft-calendar>
                </span>
              </add-to-calendar>

              <a :href="contactNextCatchupMailtoLink" class="btn btn-default mailtoui">
                <i class="fa fa-paper-plane mr-2"></i>
                Send email reminder to
                {{ contact.firstName }}
              </a>
              <!-- send contact whatsapp reminder -->
              <!-- see https://faq.whatsapp.com/en/android/26000030/ -->
            </div>

            <template slot="footer">
              <div class="row" v-if="contact.nextCatchup">
                <div class="col-6">
                  <button v-on:click="displayCreateCatchupModal = true" class="btn btn-default">
                    <i class="fa fa-calendar mr-2"></i> Schedule another
                    catch-up
                  </button>
                </div>

                <div class="col-6 text-right">
                  <router-link :to="`/catch-ups/${contact.nextCatchup.id}`" class="btn btn-info">
                    <i class="fa fa-pencil mr-2"></i> Amend this catch-up
                  </router-link>
                </div>
              </div>
            </template>
          </card>
        </div>

        <div class="col-md-6">
          <div v-if="contact && catchups && catchups.length > 0">
            <h5
              class="mb-0"
            >
              Your catch-ups with {{ contact.firstName }}
              <button class="ml-2 btn btn-link" v-on:click="displayCreateCatchupModal = true"><i class="fa fa-plus"></i></button>
            </h5>
            <time-line type="simple" v-if="catchups && catchups.length > 0">
              <time-line-item
                inverted
                :badge-icon="`fa ${catchupIcons[catchup.type]}`"
                badge-type="default"
                :badge-background-color="
                  randomColor({
                    luminosity: 'dark',
                    seed: catchup.id
                  })
                "
                v-for="catchup in catchups"
                v-bind:key="catchup.id"
              >
                <router-link slot="header" :to="`/catch-ups/${catchup.id}`">
                  <span
                    class="badge badge-default"
                    :style="
                      `background-color: ${randomColor({
                        luminosity: 'dark',
                        seed: catchup.id
                      })}`
                    "
                  >
                    {{ catchup.type || "Catch-up" | humanize }}
                    <span
                      v-if="catchup.location"
                    >at {{ catchup.location }}</span>
                  </span>
                </router-link>

                <div slot="content">
                  <vue-markdown
                    v-if="dateIsInFuture(catchup.date) && catchup.beforeNote"
                    :source="catchup.beforeNote"
                  ></vue-markdown>
                  <vue-markdown
                    v-else-if="!dateIsInFuture(catchup.date) && catchup.afterNote"
                    :source="catchup.afterNote"
                  ></vue-markdown>
                </div>
                <div slot="footer" class="row">
                  <div class="col-sm-8">
                    <span>
                      {{
                      catchup.date
                      | moment("calendar", "now", calendarFormats)
                      }}
                    </span>
                  </div>

                  <div class="col-sm-4 text-right">
                    <router-link :to="`/catch-ups/${catchup.id}`" class="link-info">
                      <i class="fa fa-arrow-right"></i>
                    </router-link>
                  </div>
                </div>
              </time-line-item>
            </time-line>
          </div>
        </div>
      </div>
    </div>

    <modal :show.sync="showCreateCatchupModal" headerClasses="justify-content-center">
      <h4 slot="header" class="title title-up">Add catch-up</h4>

      <div>
        <div class="form-group row">
          <label class="col-sm-4 col-form-label">Date</label>

          <fg-input class="col-sm-8">
            <el-date-picker
              type="date"
              v-validate="'required'"
              data-vv-as="date"
              name="date"
              placeholder="Catch-up date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              v-model="newCatchup.date"
            ></el-date-picker>
          </fg-input>
        </div>

        <div class="form-group row">
          <label class="col-sm-4 col-form-label">Catch-up type</label>

          <div class="col-sm-8">
            <select v-model="newCatchup.type" class="form-control">
              <option value="in_person">In person meetup</option>
              <option value="phone_call">Phone call</option>
              <option value="video_call">Video call (Skype, Zoom etc.)</option>
              <option value="messaging">Messaging (WhatsApp, Messenger etc.)</option>
              <option value="email">Email</option>
              <option value="other">Other</option>
              <option :value="null">Not specified</option>
            </select>
          </div>
        </div>

        <div class="form-group row" v-if="newCatchup.type === 'in_person'">
          <label class="col-sm-4 col-form-label">Location</label>
          <fg-input
            class="form-control-md col-sm-8"
            v-model="newCatchup.location"
            type="text"
            name="location"
            placeholder="e.g. Blue Moon Pub"
            addon-left-icon="now-ui-icons location_pin"
          ></fg-input>
        </div>

        <div class="form-group row">
          <label class="col-sm-4 col-form-label">Notes</label>
          <div class="col-sm-8">
            <textarea
              v-if="!newCatchup.date || newCatchupInFuture"
              class="form-control"
              v-model="newCatchup.beforeNote"
              placeholder="Remember to ask about..."
              rows="5"
            ></textarea>
            <textarea
              v-else-if="newCatchup.date && !newCatchupInFuture"
              class="form-control"
              v-model="newCatchup.afterNote"
              placeholder="Need to follow up our discussion about..."
              rows="5"
            ></textarea>
            <small class="form-text text-muted">
              Markdown supported (
              <a
                href="https://www.markdownguide.org/basic-syntax/"
                target="_blank"
              >syntax guide</a>)
            </small>
          </div>
        </div>

        <div class="alert alert-warning" v-if="newCatchupError">{{ newCatchupError }}</div>
      </div>
      <template slot="footer">
        <n-button @click.native="displayCreateCatchupModal = false">
          <i class="fa fa-times"></i>&nbsp; Cancel
        </n-button>
        <n-button type="success" @click.native="createCatchup" :disabled="creatingCatchup">
          <span v-if="!creatingCatchup">
            <i class="fa fa-check"></i>&nbsp; Save
          </span>
          <span v-else>
            <i class="fa fa-circle-o-notch fa-spin"></i>&nbsp; Please wait&hellip;
          </span>
        </n-button>
      </template>
    </modal>
  </div>
</template>

<script>
import VueMarkdown from "vue-markdown";
import moment from "moment";
import AddToCalendar from "vue-add-to-calendar";

import ContactCard from "src/components/Cards/ContactCard.vue";
import { Modal, TimeLine, TimeLineItem } from "src/components";
import { DatePicker } from "element-ui";

import MailtoUI from "mailtoui/dist/mailtoui-min.js";

export default {
  props: {
    contactId: {
      type: String,
      required: true
    },
    openCreateCatchupModal: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ContactCard,
    VueMarkdown,
    [DatePicker.name]: DatePicker,
    Modal,
    AddToCalendar,
    TimeLine,
    TimeLineItem
  },
  mounted() {
    this.loadContact();
    this.loadCatchups();
  },
  data() {
    return {
      moment,
      contact: null,
      loadingContact: false,
      newCatchup: {
        date: null,
        location: null,
        type: "in_person",
        beforeNote: null
      },
      newCatchupError: null,
      creatingCatchup: false,
      displayCreateCatchupModal: null,
      catchups: [],
      calendarFormats: {
        sameDay: "[today]",
        nextDay: "[tomorrow]",
        nextWeek: "dddd",
        lastDay: "[yesterday]",
        lastWeek: "[last] dddd",
        sameElse: "DD MMM YYYY"
      },
      catchupIcons: {
        [null]: "fa-circle",
        in_person: "fa-users",
        phone_call: "fa-phone",
        video_call: "fa-video-camera",
        messaging: "fa-comments-o",
        email: "fa-envelope",
        other: "fa-circle"
      }
    };
  },
  computed: {
    showCreateCatchupModal: {
      get: function() {
        if (this.openCreateCatchupModal && this.displayCreateCatchupModal === null) {
          return true;
        } else if (this.openCreateCatchupModal && this.displayCreateCatchupModal === true) {
          return true;
        }  else if (!this.openCreateCatchupModal && this.displayCreateCatchupModal === true) {
          return true;
        } else {
          return false;
        }
      },
      set: function (newValue) {
        this.displayCreateCatchupModal = newValue;
      }
    },
    newCatchupInFuture: function() {
      if (this.newCatchup.date) {
        if (moment(this.newCatchup.date, "YYYY-MM-DD").isAfter()) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    contactNextCatchupMailtoLink: function() {
      if (this.contact.nextCatchup) {
        let str = "mailto:";

        // email address
        let emailAddress = "";
        if (this.contact.emails) {
          this.contact.emails.forEach(item => {
            if (item.isPrimary) {
              emailAddress = item.address;
            } else {
              if (!emailAddress) {
                emailAddress = item.address;
              }
            }
          });
        }

        if (emailAddress) {
          str += emailAddress.trim();
        }

        str += "?";

        // subject
        let subject = `Catch-up ${moment(
          this.contact.nextCatchup.date,
          "YYYY-MM-DD"
        ).calendar(moment(), this.calendarFormats)}`;

        str += "subject=";
        str += encodeURIComponent(subject);

        // body
        let body = "";
        body += `Hey ${this.contact.firstName},\n\n`;
        body += "Still good for ";
        if (this.contact.nextCatchup.type === "in_person") {
          body += "a meetup";
        } else if (this.contact.nextCatchup.type === "phone_call") {
          body += "a phone call";
        } else if (this.contact.nextCatchup.type === "video_call") {
          body += "a video call";
        } else if (this.contact.nextCatchup.type === "messaging") {
          body += "an IM session";
        } else if (this.contact.nextCatchup.type === "email") {
          body += "an email session";
        } else {
          body += "a catchup";
        }

        body += " ";

        body += moment(this.contact.nextCatchup.date, "YYYY-MM-DD").calendar(
          moment(),
          this.calendarFormats
        );

        body += "?";

        body += "\n\n";
        body += "Yours,\n";
        body += this.user.firstName || "[YOUR NAME HERE]";

        str += "&";
        str += "body=";
        str += encodeURIComponent(body);

        return str;
      }
    },
    user: function() {
      return this.$auth.user();
    }
  },
  methods: {
    dateIsInFuture: function(date) {
      return moment(date).isAfter(moment());
    },
    randomColor: function(args) {
      return this.$randomColor(args);
    },
    loadContact: function() {
      this.loadingContact = true;
      this.axios
        .get(`/v1/contacts/${this.contactId}`)
        .then(response => {
          this.loadingContact = false;
          this.contact = response.data;

          // run MailtoUI
          this.$nextTick(() => {
            MailtoUI.run();
          });
        })
        .catch(error => {
          this.loadingContact = false;
          // TODO: handle error in UI
        });
    },
    loadCatchups: function() {
      this.loadingCatchups = true;
      this.axios
        .get(`/v1/catchups/?contactId=${this.contactId}&sort=-date`)
        .then(response => {
          this.loadingCatchups = false;
          this.catchups = response.data;
        })
        .catch(error => {
          this.loadingCatchups = false;
          // TODO: handle error in UI
        });
    },
    refreshContact: function(contact) {
      if (contact) {
        this.contact = contact;
      } else {
        this.loadContact();
      }
    },
    handleContactDeletion: function(contact) {
      this.$router.push({ path: "/contacts" });
    },
    createCatchup: function() {
      const instance = this;

      instance.creatingCatchup = true;

      const requestBody = {
        contactId: instance.contactId
      };

      if (instance.newCatchup.date) {
        requestBody.date = instance.newCatchup.date;
      }

      if (instance.newCatchup.type) {
        requestBody.type = instance.newCatchup.type;
      }

      if (
        instance.newCatchup.location &&
        instance.newCatchup.location.trim() !== ""
      ) {
        requestBody.location = instance.newCatchup.location;
      }

      if (
        instance.newCatchup.beforeNote &&
        instance.newCatchup.beforeNote.trim() !== ""
      ) {
        requestBody.beforeNote = instance.newCatchup.beforeNote;
      }

      if (
        instance.newCatchup.afterNote &&
        instance.newCatchup.afterNote.trim() !== ""
      ) {
        requestBody.afterNote = instance.newCatchup.afterNote;
      }

      instance.axios
        .post("/v1/catchups", requestBody)
        .then(response => {
          // delay to wait for API's afterCreate hook
          setTimeout(() => {
            instance.creatingCatchup = false;
            instance.loadContact(); // reload contact so that nextCatchup or previousCatchup fields are set by API
            this.loadCatchups();

            instance.displayCreateCatchupModal = false; // close modal

            // reset newCatchup to default values
            instance.newCatchup = {
              date: null,
              location: null,
              type: "in_person",
              beforeNote: null
            };

            // TODO: add toast notification
          }, 1000);
        })
        .catch(error => {
          instance.creatingCatchup = false;
          // TODO: handle error in UI
        });
    }
  }
};
</script>

<style>
.mailtoui-brand {
  display: none;
}
</style>
